<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import AccountPasswordResetForm from "@/components/Account/AccountPasswordResetForm.vue";
import CreateAccountForm from "@/components/Account/CreateAccountForm.vue";
import EmployeeCustomerAccess from "@/enumerations/Security/EmployeeCustomerAccess";
import { buildAddress } from "@/helpers/Account/AddressHelpers";
import { keywordMatchesSearchText } from "@/helpers/Search/SearchHelpers";
import type WebLoginAccount from "@/interfaces/WebLogin/WebLoginAccount";
import { setAccountLastLogin } from "@/repositories/Web/WebLoginRepository";
import { getAccounts, removeAccount } from "@/repositories/Web/WebMapRepository";
import { useAuthStore } from "@/stores/auth";

type DialogLayer = "search" | "select" | "detail" | "add" | "password";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const authStore = useAuthStore();
const { xs } = useDisplay();

const customerId = ref("");
const customerName = ref("");
const accounts = ref<WebLoginAccount[]>([]);
const loading = ref(false);
const validSearch = ref(true);
const layer = ref<DialogLayer>("select");
const searchText = ref("");
const sortBy = ref<"name" | "id">("name");
const selectedAccount = ref<WebLoginAccount | undefined>();
const removeDialog = ref(false);
const removing = ref(false);
const previousLayer = ref<DialogLayer>("select");

const canSearch = computed(() => {
  return (
    authStore.webLogin.isEmployee &&
    authStore.webLogin.employeeCustomerAccess === EmployeeCustomerAccess.all
  );
});

const forceSelectAccount = computed(() => !authStore.selectedAccount);

const managerName = computed(() => authStore.webLogin.employeeManagerName);

const filteredAccounts = computed(() => {
  return [...accounts.value]
    .filter((account) => {
      if (!searchText.value) {
        return true;
      }

      return (
        keywordMatchesSearchText(account.name, searchText.value) ||
        keywordMatchesSearchText(account.id, searchText.value)
      );
    })
    .sort((a, b) =>
      sortBy.value === "name"
        ? a.name.localeCompare(b.name)
        : a.id.localeCompare(b.id)
    );
});

const canRemoveSelectedAccount = computed(() => {
  return selectedAccount.value?.id !== authStore.selectedAccount?.id;
});

function resetDialogState() {
  customerId.value = "";
  customerName.value = "";
  searchText.value = "";
  sortBy.value = "name";
  selectedAccount.value = undefined;
  removeDialog.value = false;
  removing.value = false;
  validSearch.value = true;
  layer.value = canSearch.value ? "search" : "select";
}

function closeDialog() {
  emit("update:modelValue", false);
}

function buildManagerAccount() {
  return {
    id: "",
    name: managerName.value || authStore.webLogin.webLoginUserName || "Manager",
    entityId: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    apEmail: "",
    creditLimt: 0,
    currentBalance: 0,
    customerType: "",
    isAccountLinkedToWebLogin: false,
    canSwitchAccounts: true,
    isCgCustomer: false,
    isOilEnergyCustomer: false,
  };
}

async function onSearch() {
  validSearch.value = true;

  if (
    canSearch.value &&
    !customerId.value.trim() &&
    !customerName.value.trim()
  ) {
    validSearch.value = false;
    return;
  }

  loading.value = true;

  try {
    const result = await getAccounts(
      customerId.value.trim(),
      customerName.value.trim(),
      canSearch.value
    );

    accounts.value = result;

    if (result.length === 1 && !canSearch.value) {
      await selectAccount(result[0]);
      return;
    }

    layer.value = "select";
  } finally {
    loading.value = false;
  }
}

async function selectAccount(account: WebLoginAccount) {
  const accountToSelect = {
    ...account,
    canSwitchAccounts:
      authStore.webLogin.isEmployee || accounts.value.length !== 1,
  };

  await authStore.setSelectedAccount(accountToSelect);

  if (accountToSelect.isAccountLinkedToWebLogin && accountToSelect.id) {
    await setAccountLastLogin(accountToSelect.id);
  }

  resetDialogState();
  closeDialog();
}

async function selectManagerAccount() {
  await authStore.setSelectedAccount(buildManagerAccount());
  resetDialogState();
  closeDialog();
}

function showAccountDetail(account: WebLoginAccount) {
  selectedAccount.value = account;
  layer.value = "detail";
}

function openAddAccount() {
  previousLayer.value = layer.value;
  layer.value = "add";
}

function closeAddAccount() {
  layer.value = previousLayer.value;
}

function openChangePassword() {
  previousLayer.value = layer.value;
  layer.value = "password";
}

function closeChangePassword() {
  layer.value = previousLayer.value;
}

async function removeSelectedAccount() {
  const account = selectedAccount.value;
  if (!account) {
    return;
  }

  removing.value = true;

  try {
    await removeAccount(account.id, account.entityId);
    accounts.value = accounts.value.filter(
      (existing) =>
        !(existing.id === account.id && existing.entityId === account.entityId)
    );
    selectedAccount.value = undefined;
    removeDialog.value = false;
    layer.value = "select";

    if (authStore.selectedAccount && accounts.value.length === 1) {
      authStore.updateSelectedAccountDetails({
        ...authStore.selectedAccount,
        canSwitchAccounts: false,
      });
    }
  } finally {
    removing.value = false;
  }
}

function cancelSelection() {
  resetDialogState();
  closeDialog();
}

async function handleAddedAccount(account: WebLoginAccount) {
  const accountToSelect = {
    ...account,
    canSwitchAccounts: true,
  };

  const isKnownAccount = accounts.value.some(
    (current) =>
      current.id === accountToSelect.id && current.entityId === accountToSelect.entityId
  );

  if (!isKnownAccount) {
    accounts.value = [...accounts.value, accountToSelect];
  }

  await authStore.setSelectedAccount(accountToSelect);

  if (accountToSelect.isAccountLinkedToWebLogin && accountToSelect.id) {
    await setAccountLastLogin(accountToSelect.id);
  }

  resetDialogState();
  closeDialog();
}

watch(
  () => props.modelValue,
  async (value) => {
    if (!value) {
      return;
    }

    resetDialogState();

    if (!canSearch.value) {
      await onSearch();
    }
  }
);
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="700"
    :fullscreen="xs"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-overlay :model-value="loading || removing" contained class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate :size="54" :width="3" />
      </v-overlay>

      <template v-if="layer === 'search'">
        <v-card-title class="d-flex align-center">
          <span>Search for an Account</span>
          <v-spacer />
          <v-menu location="bottom end">
            <template #activator="{ props: menuProps }">
              <v-btn v-bind="menuProps" icon="mdi-cog" variant="text" v-track="'login.accountSelectionDialog.openMenu'" />
            </template>
            <v-list density="compact">
              <v-list-item v-track="'login.accountSelectionDialog.menuAddAccount'" @click="openAddAccount">
                <v-list-item-title>Add Account</v-list-item-title>
              </v-list-item>
              <v-list-item v-track="'login.accountSelectionDialog.menuChangePassword'" @click="openChangePassword">
                <v-list-item-title>Change Password</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-alert
            v-if="!validSearch"
            class="mb-4"
            type="error"
            variant="tonal"
          >
            Please enter a customer name or customer ID to search.
          </v-alert>

          <v-text-field
            v-model="customerName"
            label="Customer Name"
            prepend-inner-icon="mdi-account"
            clearable
          />
          <v-text-field
            v-model="customerId"
            label="Customer ID"
            prepend-inner-icon="mdi-key"
            clearable
            @keyup.enter="onSearch"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!forceSelectAccount" variant="text" v-track="'login.accountSelectionDialog.searchCancel'" @click="cancelSelection">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn color="primary" v-track="'login.accountSelectionDialog.search'" @click="onSearch">Search</v-btn>
        </v-card-actions>
      </template>

      <template v-else-if="layer === 'password'">
        <AccountPasswordResetForm
          title="Change Password"
          @on-cancel="closeChangePassword"
          @account-password-reset-form-success="closeChangePassword"
        />
      </template>

      <template v-else-if="layer === 'add'">
        <CreateAccountForm
          :is-adding-account-to-web-login="true"
          :on-changed="layer === 'add'"
          title="Add Account"
          @on-cancel="closeAddAccount"
          @on-added="handleAddedAccount"
        />
      </template>

      <template v-else-if="layer === 'select'">
        <v-card-title class="d-flex align-center">
          <span>Select an Account</span>
          <v-spacer />
          <v-btn-toggle v-model="sortBy" mandatory density="compact">
            <v-btn value="name" v-track="'login.accountSelectionDialog.sortByName'">Sort Name</v-btn>
            <v-btn value="id" v-track="'login.accountSelectionDialog.sortById'">Sort ID</v-btn>
          </v-btn-toggle>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="searchText"
            class="mb-4"
            label="Search Accounts"
            prepend-inner-icon="mdi-magnify"
            clearable
          />

          <v-list
            density="comfortable"
            lines="two"
            max-height="420"
            class="overflow-y-auto"
          >
            <v-list-item
              v-if="authStore.webLogin.isEmployee && !canSearch && !searchText && managerName"
              prepend-icon="mdi-account-tie-outline"
              v-track="'login.accountSelectionDialog.selectManagerAccount'"
              @click="selectManagerAccount"
            >
              <v-list-item-title>{{ managerName }}</v-list-item-title>
              <v-list-item-subtitle>Manager account</v-list-item-subtitle>
            </v-list-item>
            <v-divider v-if="authStore.webLogin.isEmployee && !canSearch && !searchText && managerName" />

            <template v-for="account in filteredAccounts" :key="`${account.id}-${account.entityId}`">
              <v-list-item v-track="'login.accountSelectionDialog.selectAccount'" @click="selectAccount(account)">
                <template #prepend>
                  <v-avatar color="primary" size="32">
                    <span class="text-white text-caption">
                      {{ account.id.slice(0, 2).toUpperCase() }}
                    </span>
                  </v-avatar>
                </template>
                <v-list-item-title>{{ account.id }} - {{ account.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ account.entityId || "No entity" }}
                </v-list-item-subtitle>
                <template #append>
                  <v-btn
                    icon="mdi-dots-vertical"
                    variant="text"
                    v-track="'login.accountSelectionDialog.showAccountDetail'"
                    @click.stop="showAccountDetail(account)"
                  />
                </template>
              </v-list-item>
              <v-divider />
            </template>

            <v-list-item v-if="filteredAccounts.length === 0">
              <v-list-item-title class="font-italic">
                No accounts were found with the current filters.
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" v-track="'login.accountSelectionDialog.addAccount'" @click="openAddAccount">Add Account</v-btn>
          <v-btn v-if="canSearch" variant="text" v-track="'login.accountSelectionDialog.returnToSearch'" @click="layer = 'search'">
            Return to Search
          </v-btn>
          <v-spacer />
          <v-btn v-if="!forceSelectAccount" variant="text" v-track="'login.accountSelectionDialog.selectCancel'" @click="cancelSelection">
            Cancel
          </v-btn>
        </v-card-actions>
      </template>

      <template v-else-if="selectedAccount">
        <v-card-title class="d-flex align-center">
          <span>{{ selectedAccount.id }}</span>
          <v-spacer />
          <v-btn icon="mdi-trash-can-outline" variant="text" v-track="'login.accountSelectionDialog.openRemoveDialog'" @click="removeDialog = true" />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-medium-emphasis text-caption">Name</div>
              <div>{{ selectedAccount.name }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-medium-emphasis text-caption">Type</div>
              <div>{{ selectedAccount.customerType || "Not provided" }}</div>
            </v-col>
          </v-row>
          <v-row v-if="selectedAccount.address1">
            <v-col cols="12">
              <div class="text-medium-emphasis text-caption">Address</div>
              <div class="pre-wrap">
                {{
                  buildAddress(
                    selectedAccount.address1,
                    selectedAccount.address2,
                    selectedAccount.city,
                    selectedAccount.state,
                    selectedAccount.zip,
                    ""
                  )
                }}
              </div>
            </v-col>
          </v-row>
          <v-row v-if="selectedAccount.apEmail">
            <v-col cols="12">
              <div class="text-medium-emphasis text-caption">Accounts Payable Email</div>
              <div>{{ selectedAccount.apEmail }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-medium-emphasis text-caption">Credit Limit</div>
              <div>{{ selectedAccount.creditLimt }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-medium-emphasis text-caption">Current Balance</div>
              <div>{{ selectedAccount.currentBalance }}</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" v-track="'login.accountSelectionDialog.detailBack'" @click="layer = 'select'">Back</v-btn>
          <v-spacer />
          <v-btn color="primary" v-track="'login.accountSelectionDialog.detailSelect'" @click="selectAccount(selectedAccount)">Select</v-btn>
        </v-card-actions>

        <v-dialog v-model="removeDialog" max-width="460">
          <v-card>
            <v-card-title>Remove Account</v-card-title>
            <v-card-text>
              <template v-if="canRemoveSelectedAccount">
                Remove this account from your login?
              </template>
              <template v-else>
                Cannot remove this account because you are currently logged into it.
              </template>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" v-track="'login.accountSelectionDialog.removeCancel'" @click="removeDialog = false">Cancel</v-btn>
              <v-btn
                v-if="canRemoveSelectedAccount"
                color="error"
                :disabled="!selectedAccount.isAccountLinkedToWebLogin"
                v-track="'login.accountSelectionDialog.removeSelectedAccount'"
                @click="removeSelectedAccount"
              >
                Remove
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.pre-wrap {
  white-space: pre-wrap;
}
</style>
